import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Search, MapPin, Star, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800" },
  verified: { label: "Verified", color: "bg-blue-100 text-blue-800" },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800" },
  suspended: { label: "Suspended", color: "bg-red-100 text-red-800" },
};

const tierConfig = {
  starter: { label: "Starter", color: "tier-starter" },
  pro: { label: "Pro", color: "tier-pro" },
  elite: { label: "Elite", color: "tier-elite" },
  master: { label: "Master", color: "tier-master" },
};

export default function AdminTherapists() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");

  useEffect(() => {
    fetchTherapists();
  }, [statusFilter, tierFilter]);

  const fetchTherapists = async () => {
    try {
      let params = [];
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (tierFilter !== "all") params.push(`tier=${tierFilter}`);
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      
      const response = await axios.get(`${API}/admin/therapists${queryString}`, { withCredentials: true });
      setTherapists(response.data);
    } catch (error) {
      console.error("Error fetching therapists:", error);
      toast.error("Failed to load therapists");
    } finally {
      setLoading(false);
    }
  };

  const filteredTherapists = therapists.filter(th =>
    th.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    th.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    th.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    th.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-therapists">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(tierConfig).map(([key, config]) => (
          <Card key={key} className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{config.label}</p>
                  <p className="text-2xl font-heading font-bold text-slate-900">
                    {therapists.filter(t => t.tier === key).length}
                  </p>
                </div>
                <Badge className={config.color}>{config.label}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-heading font-bold text-primary">{therapists.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="tier-filter">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
                <SelectItem value="master">Master</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Therapists Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Therapists ({filteredTherapists.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto table-scroll">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Therapist</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Monthly Earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTherapists.map((th) => (
                  <TableRow key={th.therapist_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={th.picture} />
                          <AvatarFallback className="bg-primary text-white">
                            {th.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{th.name}</p>
                          <p className="text-xs text-slate-500">{th.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{th.role_type}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {th.city}, {th.state}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[th.status]?.color}>
                        {statusConfig[th.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={tierConfig[th.tier]?.color}>
                        {tierConfig[th.tier]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span>{th.performance_score}%</span>
                          <TrendingUp className="w-3 h-3 text-emerald-500" />
                        </div>
                        <Progress value={th.performance_score} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="font-medium">{th.rating?.toFixed(1)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-slate-900">
                        RM {th.monthly_earnings?.toLocaleString()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
